// src/pages/LandingPage.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../assets/css/landing.css';

const LandingPage = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    {
      icon: 'fa-camera',
      title: 'Selfie Check-In',
      description: 'Seamless event check-in with selfie capture. We match your selfie with your moments automatically.',
    },
    {
      icon: 'fa-camera',
      title: 'Auto-Capture Moments',
      description: 'Advanced AI captures your best moments so you can stay fully present at the event.',
    },
    {
      icon: 'fa-user-circle',
      title: 'Personalized Experience',
      description: 'Receive customized event footage and photos tailored to your attendance.',
    },
    {
      icon: 'fa-rocket',
      title: 'Event Promotion',
      description: 'Powerful tools for promoters to manage, market, and grow their events.',
    },
    {
      icon: 'fa-bar-chart',
      title: 'Smart Analytics',
      description: 'Track attendance, engagement, and event performance in real-time.',
    },
    {
      icon: 'fa-globe',
      title: 'Global Reach',
      description: 'Connect with events worldwide and build your event portfolio.',
    },
  ];

  const howItWorks = [
    {
      step: '01',
      title: 'Download & Register',
      description: 'Get the Spono app and create your free account in seconds.',
      icon: 'fa-mobile',
    },
    {
      step: '02',
      title: 'Discover Events',
      description: 'Browse amazing events near you or around the world.',
      icon: 'fa-search',
    },
    {
      step: '03',
      title: 'Selfie Check-In',
      description: 'Take a selfie to check in and we\'ll match it with your moments throughout the event.',
      icon: 'fa-camera',
    },
    {
      step: '04',
      title: 'Enjoy the Moment',
      description: 'Stay present while we capture and deliver your personalized footage.',
      icon: 'fa-star',
    },
  ];

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-gradient"></div>
        <div className="container">
          <div className="row align-items-center min-vh-100">
            <div className="col-lg-6">
              <div className="hero-content">
                <div className="hero-badge mb-4">
                  <span className="badge-glow"></span>
                  <span className="badge-text">Revolutionizing Event Experiences</span>
                </div>
                <h1 className="hero-title">
                  Enjoy the <span className="gradient-text">Moment</span>
                  <br />
                  While We <span className="gradient-text">Capture</span> It
                </h1>
                <p className="hero-subtitle">
                  SPONO transforms how you experience events. Check in with a selfie,
                  stay fully present, and receive personalized event footage automatically matched to you.
                </p>
                <div className="hero-buttons">
                  <a 
                    href="#download" 
                    className="btn btn-primary btn-lg spono-btn-gradient"
                  >
                    Download App
                  </a>
                  <Link 
                    to="/login" 
                    className="btn btn-outline-light btn-lg spono-btn-outline"
                  >
                    Event Promoter Login
                  </Link>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="hero-image">
                <div className="phone-mockup">
                  <div className="phone-frame">
                    <div className="phone-screen">
                      <div className="app-preview">
                        <div className="preview-header">
                          <span className="preview-logo">SPONO</span>
                          <div className="preview-notification">
                            <span className="notification-badge">3</span>
                          </div>
                        </div>
                        <div className="preview-content">
                          <div className="preview-card"></div>
                          <div className="preview-card"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="floating-element element-1">
                    <i className="fa fa-camera"></i>
                  </div>
                  <div className="floating-element element-2">
                    <i className="fa fa-qrcode"></i>
                  </div>
                  <div className="floating-element element-3">
                    <i className="fa fa-star"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section py-5">
        <div className="container">
          <div className="section-header text-center mb-5">
            <h2 className="section-title">
              Why Choose <span className="gradient-text">SPONO</span>?
            </h2>
            <p className="section-subtitle">
              Experience events like never before with cutting-edge technology
            </p>
          </div>
          <div className="row g-4">
            {features.map((feature, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <div className="feature-card">
                  <div className="feature-icon">
                    <i className={`fa ${feature.icon}`}></i>
                  </div>
                  <h3 className="feature-title">{feature.title}</h3>
                  <p className="feature-description">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="how-it-works-section py-5">
        <div className="container">
          <div className="section-header text-center mb-5">
            <h2 className="section-title">
              How <span className="gradient-text">SPONO</span> Works
            </h2>
            <p className="section-subtitle">
              Getting started is simple and takes only minutes
            </p>
          </div>
          <div className="row g-4">
            {howItWorks.map((item, index) => (
              <div key={index} className="col-md-6 col-lg-3">
                <div className="process-card">
                  <div className="process-step">{item.step}</div>
                  <div className="process-icon">
                    <i className={`fa ${item.icon}`}></i>
                  </div>
                  <h3 className="process-title">{item.title}</h3>
                  <p className="process-description">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For Event Attendees Section */}
      <section className="attendees-section py-5">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <div className="section-image">
                <div className="image-card">
                  <i className="fa fa-users section-icon"></i>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="content-block">
                <h2 className="section-title mb-4">
                  For <span className="gradient-text">Event Attendees</span>
                </h2>
                <p className="lead mb-4">
                  Never miss a moment while trying to capture it. SPONO lets you stay fully present at events while our technology works in the background.
                </p>
                <ul className="benefits-list">
                  <li>
                    <i className="fa fa-check-circle gradient-text"></i>
                    <span>Selfie check-in for instant facial recognition</span>
                  </li>
                  <li>
                    <i className="fa fa-check-circle gradient-text"></i>
                    <span>Automatic moment capture and delivery</span>
                  </li>
                  <li>
                    <i className="fa fa-check-circle gradient-text"></i>
                    <span>Personalized event footage matched to you</span>
                  </li>
                  <li>
                    <i className="fa fa-check-circle gradient-text"></i>
                    <span>Share memories instantly</span>
                  </li>
                  <li>
                    <i className="fa fa-check-circle gradient-text"></i>
                    <span>Build your event portfolio</span>
                  </li>
                </ul>
                <a href="#download" className="btn btn-lg spono-btn-gradient mt-4">
                  <i className="fa fa-download me-2"></i>
                  Get the App Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Event Promoters Section */}
      <section className="promoters-section py-5">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 order-lg-2 mb-4 mb-lg-0">
              <div className="section-image">
                <div className="image-card">
                  <i className="fa fa-rocket section-icon"></i>
                </div>
              </div>
            </div>
            <div className="col-lg-6 order-lg-1">
              <div className="content-block">
                <h2 className="section-title mb-4">
                  For <span className="gradient-text">Event Promoters</span>
                </h2>
                <p className="lead mb-4">
                  Powerful tools to manage, promote, and grow your events. Engage attendees like never before with data-driven insights.
                </p>
                <ul className="benefits-list">
                  <li>
                    <i className="fa fa-check-circle gradient-text"></i>
                    <span>Easy event creation and management</span>
                  </li>
                  <li>
                    <i className="fa fa-check-circle gradient-text"></i>
                    <span>Selfie-based check-in system for attendees</span>
                  </li>
                  <li>
                    <i className="fa fa-check-circle gradient-text"></i>
                    <span>Real-time attendance tracking</span>
                  </li>
                  <li>
                    <i className="fa fa-check-circle gradient-text"></i>
                    <span>Advanced analytics and insights</span>
                  </li>
                  <li>
                    <i className="fa fa-check-circle gradient-text"></i>
                    <span>Automated content delivery to attendees</span>
                  </li>
                </ul>
                <div className="mt-4">
                  <Link to="/signup" className="btn btn-lg spono-btn-gradient me-3">
                    <i className="fa fa-user-plus me-2"></i>
                    Register as Promoter
                  </Link>
                  <Link to="/login" className="btn btn-lg spono-btn-outline">
                    <i className="fa fa-sign-in me-2"></i>
                    Login
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section id="download" className="download-section py-5">
        <div className="container">
          <div className="download-card">
            <div className="row align-items-center">
              <div className="col-lg-6 text-center text-lg-start mb-4 mb-lg-0">
                <h2 className="section-title mb-3">
                  Ready to Experience <span className="gradient-text">SPONO</span>?
                </h2>
                <p className="lead mb-4">
                  Download the app now and start capturing unforgettable moments at your next event!
                </p>
                <div className="download-buttons">
                  <a 
                    href="https://apps.apple.com/app/spono" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="store-button"
                  >
                    <i className="fa fa-apple"></i>
                    <div>
                      <small>Download on the</small>
                      <strong>App Store</strong>
                    </div>
                  </a>
                  <a 
                    href="https://play.google.com/store/apps/details?id=com.spono.ai" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="store-button"
                  >
                    <i className="fa fa-android"></i>
                    <div>
                      <small>GET IT ON</small>
                      <strong>Google Play</strong>
                    </div>
                  </a>
                </div>
              </div>
              <div className="col-lg-6 text-center">
                <div className="qr-code-container">
                  <img src="/spono-qr-code.svg" alt="Scan to Download Spono" className="qr-code-img" />
                  <p className="mt-3 text-white-50">Scan to download the app</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-5">
        <div className="container text-center">
          <h2 className="section-title mb-4">
            Join Thousands of Event Enthusiasts
          </h2>
          <p className="lead mb-4">
            Whether you're attending events or promoting them, SPONO is your ultimate companion
          </p>
          <div className="cta-buttons">
            <a href="#download" className="btn btn-lg spono-btn-gradient me-3">
              Download App
            </a>
            <Link to="/about" className="btn btn-lg spono-btn-outline">
              Learn More
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
